<script setup lang="ts">
    import InputLabel from '../InputLabel.vue';
    import {
        EyeIcon,
        EyeOffIcon,
        UserIcon,
        CirclePlusIcon,
        CircleMinusIcon,
        RotateCcwIcon,
        ShieldAlertIcon,
        ShieldCheckIcon,
    } from '@lucide/vue';
    import TextInput from '../TextInput.vue';
    import CheckboxInput from '../CheckboxInput.vue';
    import { TextInputWidth } from '../TextInput.vue';
    import SelectInput from '../SelectInput.vue';
    import PanelTabs from '../PanelTabs.vue';
    import PanelTab from '../PanelTab.vue';
    import { __ } from '@/js/stores/expressions';
    import { ref, watch, onMounted, computed, toRaw } from 'vue';
    import { ResponseGetUser, OrgAccessSelection } from '@/js/services/api/user/types';
    import { getRoles } from '@/js/services/api/user/userApi';
    import EchoButton from '../EchoButton.vue';
    import { ApiRole, RoleOrgAccessLevel } from '@/js/services/api/role/types';
    import type { AccessGroupNode, AccessLevel } from '@/js/services/api/role/types';
    import {
        useQueryGetRolePermissions,
        useQueryGetRole,
    } from '@/js/services/api/role/roleQueries';
    import ModuleAccessControl from '../roles/ModuleAccessControl.vue';
    import OrgDataAccessControl from '../roles/OrgDataAccessControl.vue';
    import PermissionsSummary from './PermissionsSummary.vue';
    import { Role, usePermissions } from '@/js/composables/usePermissions';

    const props = defineProps<{
        user?: ResponseGetUser | null;
    }>();

    const roles = ref<ApiRole[]>([]);
    const emit = defineEmits(['save', 'reset-overrides']);

    const { hasRole } = usePermissions();

    // The user diverges from its role; gate the reset action so it's only
    // offered when there's actually something to revert.
    const hasOverride = computed(() => !!props.user?.access?.hasOverride);

    const form = ref({
        firstName: '',
        lastName: '',
        email: '',
        emailVerified: false,
        password: '',
        setTemporaryPassword: false,
        roleId: '',
    });

    const userPermissions = ref<Record<string, number>>({});
    const initialUserPermissions = ref<Record<string, number>>({});

    const access = computed(() => props.user?.access?.permissions);

    const { data: selectedRolePermissions } = useQueryGetRolePermissions(() => form.value.roleId);

    // True while the user has switched to a different role but its permissions
    // haven't been fetched yet. Saving in this state would silently keep the
    // previous role's user-level overrides — see submit() and canSubmit.
    const isAwaitingNewRolePermissions = computed(
        () => !!props.user && !isOriginalRoleSelected.value && !selectedRolePermissions.value
    );

    const isOriginalRoleSelected = computed(
        () => !!props.user && form.value.roleId === props.user.role?.id
    );

    const compareToPermissions = computed<Record<string, number> | undefined>(() => {
        if (isOriginalRoleSelected.value) return access.value?.role;
        return selectedRolePermissions.value?.rolePermissions;
    });

    // The permission schema is role-independent and no longer embedded in the
    // detailed user response, so source it from the role-permissions query
    // (fetched for whichever role is currently selected, including the original).
    const moduleDefinitions = computed(() => selectedRolePermissions.value?.permissions);

    const selectedRole = computed(() => roles.value.find((r) => r.id === form.value.roleId));

    const selectedRoleName = computed(() => selectedRole.value?.name ?? '');

    // Mirroring a role's own access makes the backend diff empty: no override stored.
    const selectedRoleDataAccess = computed<OrgAccessSelection | undefined>(() => {
        const role = selectedRole.value;
        return role
            ? { roleOrgAccessType: role.roleOrgAccessType, ownTeamAgg: role.ownTeamAgg }
            : undefined;
    });

    // Keep the original role's existing override; otherwise send the role
    // mirror so switching can't persist a stale override.
    const effectiveDataAccess = computed<OrgAccessSelection | undefined>(() =>
        isOriginalRoleSelected.value
            ? (props.user?.access?.dataAccess?.effective ?? selectedRoleDataAccess.value)
            : selectedRoleDataAccess.value
    );

    // The role's own access level, used by the summary tab to flag a per-user
    // override of the level. Mirrors compareToPermissions: the original role's
    // stored baseline, or the freshly selected role's own level.
    const roleAccessLevel = computed<RoleOrgAccessLevel | undefined>(() =>
        isOriginalRoleSelected.value
            ? props.user?.access?.dataAccess?.role?.roleOrgAccessType
            : selectedRoleDataAccess.value?.roleOrgAccessType
    );

    // The role's own team-aggregate flag, the baseline for the checkbox's
    // add/remove badge in the Data tab. Mirrors roleAccessLevel's sourcing.
    const roleAgentGroups = computed<boolean>(() =>
        isOriginalRoleSelected.value
            ? (props.user?.access?.dataAccess?.role?.ownTeamAgg ?? false)
            : (selectedRoleDataAccess.value?.ownTeamAgg ?? false)
    );

    const userDisplayName = computed(() => {
        if (!props.user) return '';
        const { firstName, lastName, email } = props.user;
        const name = [firstName, lastName].filter(Boolean).join(' ');
        return name || email || '';
    });

    // ---- Org-tree (data access) override state ----------------------------
    const accessLevel = ref<AccessLevel>(undefined);
    const accessGroups = ref<AccessGroupNode[]>([]);
    const initialAccessLevel = ref<AccessLevel>(undefined);
    const initialAccessGroups = ref<AccessGroupNode[]>([]);
    // The role's own tree for whichever role is currently displayed. Selecting
    // Custom resets the editor to this, so the user customizes from the role
    // defaults rather than their current effective access.
    const roleOrgBaseline = ref<AccessGroupNode[]>([]);
    // Read-only role baseline, flattened by node id, for the per-node badges.
    const orgBaselineById = ref<Map<string, AccessGroupNode>>(new Map());
    // Per-user override of the role's "team aggregate access" flag, editable via
    // the checkbox in OrgDataAccessControl. initial* is the baseline for change
    // detection (see hasDataAccessChanged).
    const ownTeamAgg = ref(false);
    const initialOwnTeamAgg = ref(false);

    // structuredClone(toRaw(...)) only unwraps the top-level proxy; nested
    // reactive children remain proxies and trip DataCloneError. The tree is
    // pure JSON-safe data, so a JSON round-trip deep-unwraps it reliably.
    const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

    const flattenById = (
        nodes: AccessGroupNode[],
        map: Map<string, AccessGroupNode> = new Map()
    ): Map<string, AccessGroupNode> => {
        for (const node of nodes) {
            map.set(node.id, node);
            if (node.children?.length) flattenById(node.children, map);
        }
        return map;
    };

    const orgAccess = computed(() => props.user?.access?.orgDataAccess);

    const rebuildOrgFromAccess = () => {
        const tree = orgAccess.value;
        const scalar = props.user?.access?.dataAccess;
        if (!tree) {
            accessLevel.value = undefined;
            accessGroups.value = [];
            initialAccessLevel.value = undefined;
            initialAccessGroups.value = [];
            roleOrgBaseline.value = [];
            orgBaselineById.value = new Map();
            ownTeamAgg.value = false;
            initialOwnTeamAgg.value = false;
            return;
        }
        const seed = tree.effective ?? tree.role ?? [];
        accessGroups.value = deepClone(seed);
        initialAccessGroups.value = deepClone(seed);
        roleOrgBaseline.value = deepClone(tree.role);
        accessLevel.value = scalar?.effective?.roleOrgAccessType ?? scalar?.role?.roleOrgAccessType;
        initialAccessLevel.value = accessLevel.value;
        orgBaselineById.value = flattenById(deepClone(tree.role));
        ownTeamAgg.value = scalar?.effective?.ownTeamAgg ?? scalar?.role?.ownTeamAgg ?? false;
        initialOwnTeamAgg.value = ownTeamAgg.value;
    };

    // Fetch the selected role's full tree so switching roles reseeds the editor.
    const { data: selectedRoleDetail } = useQueryGetRole(() => form.value.roleId);

    const isAwaitingNewRoleOrgTree = computed(
        () => !!props.user && !isOriginalRoleSelected.value && !selectedRoleDetail.value
    );

    const hasDataAccessChanged = () =>
        accessLevel.value !== initialAccessLevel.value ||
        ownTeamAgg.value !== initialOwnTeamAgg.value ||
        JSON.stringify(toRaw(accessGroups.value)) !== JSON.stringify(initialAccessGroups.value);

    // The pair to save: the Data tab's live state once its tree has loaded, else the selected
    // role's baseline. Undefined blocks submit, because a stand-in value would read as a
    // deliberate pick and store an override narrowing anyone whose role grants more.
    const dataAccessToSave = computed<OrgAccessSelection | undefined>(() =>
        accessLevel.value === undefined
            ? effectiveDataAccess.value
            : { roleOrgAccessType: accessLevel.value, ownTeamAgg: ownTeamAgg.value }
    );

    const rebuildFromAccess = () => {
        if (!access.value) {
            userPermissions.value = {};
            initialUserPermissions.value = {};
            return;
        }
        const merged = { ...access.value.role, ...access.value.user };
        userPermissions.value = { ...merged };
        initialUserPermissions.value = { ...merged };
    };

    const resetArea = (areaName: string) => {
        const baseline = compareToPermissions.value;
        if (!baseline) return;
        userPermissions.value = {
            ...userPermissions.value,
            [areaName]: baseline[areaName] ?? 0,
        };
    };

    const havePermissionsChanged = () => {
        const current = userPermissions.value;
        const initial = initialUserPermissions.value;
        const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);
        return [...keys].some((key) => current[key] !== initial[key]);
    };

    const showPassword = ref(false);

    const canSubmit = computed(() => {
        if (!form.value.email) {
            return false;
        }

        if (!props.user) {
            return Boolean(form.value.password);
        }
        if (isAwaitingNewRolePermissions.value) {
            return false;
        }
        if (isAwaitingNewRoleOrgTree.value) {
            return false;
        }
        if (!dataAccessToSave.value) {
            return false;
        }
        return true;
    });

    onMounted(async () => {
        const result = await getRoles();
        roles.value = result.roles;
    });

    watch(
        () => props.user?.id,
        () => {
            if (!props.user) return;

            form.value = {
                firstName: props.user.firstName,
                lastName: props.user.lastName,
                email: props.user.email,
                emailVerified: props.user.emailVerified ?? false,
                password: '',
                setTemporaryPassword: false,
                roleId: props.user.role?.id ?? '',
            };
            rebuildFromAccess();
            rebuildOrgFromAccess();
        },
        { immediate: true }
    );

    watch(() => props.user?.access?.permissions, rebuildFromAccess, { deep: true });
    watch(() => props.user?.access?.orgDataAccess, rebuildOrgFromAccess, { deep: true });

    // When a different role is selected, swap the displayed tree to that role's
    // org access (no override yet). Switching back to the original restores any
    // stored user-level override via rebuildOrgFromAccess.
    watch([() => form.value.roleId, () => selectedRoleDetail.value], ([, roleDetail]) => {
        if (!props.user) return;
        if (isOriginalRoleSelected.value) {
            rebuildOrgFromAccess();
        } else if (roleDetail) {
            const tree = roleDetail.orgDataAccessTree ?? [];
            accessGroups.value = deepClone(tree);
            roleOrgBaseline.value = deepClone(tree);
            accessLevel.value = roleDetail.roleOrgAccessType;
            orgBaselineById.value = flattenById(deepClone(tree));
            ownTeamAgg.value = roleDetail.ownTeamAgg;
        }
    });

    // When the user picks a different role from the original, swap the
    // displayed tree to that role's permissions. Switching back to the
    // original role restores any prior user-level overrides via rebuildFromAccess.
    watch(
        [() => form.value.roleId, () => selectedRolePermissions.value?.rolePermissions],
        ([, newRolePerms]) => {
            if (!props.user) return;
            if (isOriginalRoleSelected.value) {
                rebuildFromAccess();
            } else if (newRolePerms) {
                userPermissions.value = { ...newRolePerms };
            }
        }
    );

    const submit = () => {
        if (!props.user && !form.value.password) {
            return;
        }
        if (isAwaitingNewRolePermissions.value) {
            return;
        }
        if (isAwaitingNewRoleOrgTree.value) {
            return;
        }
        if (props.user && !dataAccessToSave.value) {
            return;
        }
        emit('save', {
            ...form.value,
            permissionsChanged: havePermissionsChanged(),
            // Backend requires every area in the dict; it diffs against the
            // tenant role internally and stores only entries that diverge.
            permissionsOverride: { ...userPermissions.value },
            dataAccess: dataAccessToSave.value,
            // Org-tree override: the backend diffs the full tree against the
            // role and stores only divergent per-group rows.
            dataAccessChanged: hasDataAccessChanged(),
            orgDataAccess: deepClone(accessGroups.value),
        });
    };

    defineExpose({
        submit,
        canSubmit,
    });
</script>

<template>
    <div class="flex items-center mb-4 gap-3">
        <UserIcon class="size-5" />
        <h2 class="heading-lg">
            {{ __('user-details') }}
        </h2>
    </div>
    <form @submit.prevent="submit">
        <div class="max-w-[720px] grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
            <div>
                <InputLabel for="firstName">
                    <span class="normal-case">
                        {{ __('user-first-name') }}
                    </span>
                </InputLabel>
                <TextInput id="firstName" v-model="form.firstName" :width="TextInputWidth.Large" />
            </div>
            <div>
                <InputLabel for="lastName">
                    <span class="normal-case">
                        {{ __('user-last-name') }}
                    </span>
                </InputLabel>
                <TextInput id="lastName" v-model="form.lastName" :width="TextInputWidth.Large" />
            </div>
            <div>
                <InputLabel for="email">
                    <span class="normal-case">
                        {{ __('user-email') }}
                    </span>
                </InputLabel>
                <TextInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    :width="TextInputWidth.Large"
                    required
                    aria-required="true"
                />
                <div class="mt-2">
                    <!-- A user without a login account has no verification to set. -->
                    <CheckboxInput
                        id="emailVerified"
                        v-model="form.emailVerified"
                        :disabled="!!user && user.emailVerified == null"
                    >
                        <span class="text-body">{{ __('verified') }}</span>
                    </CheckboxInput>
                </div>
            </div>
            <div>
                <InputLabel for="role">
                    <span class="normal-case">
                        {{ __('user-role') }}
                    </span>
                </InputLabel>
                <SelectInput id="role" v-model="form.roleId" class="w-80">
                    <option disabled value="">
                        {{ __('select-choose') }}
                    </option>
                    <option
                        v-for="role in roles"
                        :key="role.id"
                        :value="role.id"
                        :aria-label="__('user-role')"
                    >
                        {{ role.name }}
                    </option>
                </SelectInput>
            </div>
            <div v-if="!user">
                <InputLabel for="password">
                    <span class="normal-case">
                        {{ __('user-password') }}
                    </span>
                </InputLabel>
                <div class="relative inline-block">
                    <TextInput
                        id="password"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        :width="TextInputWidth.Large"
                        required
                        aria-required="true"
                    />
                    <button
                        type="button"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-gray cursor-pointer"
                        :aria-label="showPassword ? __('hide-password') : __('show-password')"
                        @click="showPassword = !showPassword"
                    >
                        <EyeOffIcon v-if="showPassword" class="size-5" />
                        <EyeIcon v-else class="size-5" />
                    </button>
                </div>
            </div>
            <!-- Temporary password support is intentionally hidden for now and will be introduced in a separate PR -->
            <!--
                <div v-if="!user" class="md:col-span-2 -mt-2">
                    <CheckboxInput
                        id="setTemporaryPassword"
                        v-model="form.setTemporaryPassword"
                        :helpHint="__('temporary-password')"
                    >
                        <span class="text-body">
                            {{ __('set-temporary-password') }}
                        </span>

                        <template #help>
                            {{ __('temporary-password-help') }}
                        </template>
                    </CheckboxInput>
                </div>
                 -->
        </div>

        <section v-if="user" class="mt-10">
            <h2 class="heading-lg">{{ __('user-permissions-heading') }}</h2>

            <!-- Shared intro + legend for all tabs; the +/−/↺ markers carry the
                 same meaning across the summary, data and functionality views. -->
            <div class="mt-4 flex flex-col gap-3">
                <p class="text-body text-gray-dark">
                    {{ __('user-access-help-intro') }}
                </p>
                <ul class="flex flex-col gap-1.5 text-caption text-gray-dark">
                    <li class="inline-flex items-center gap-2">
                        <CirclePlusIcon class="size-4 text-primary-carrot shrink-0" />
                        {{ __('user-permissions-legend-added') }}
                    </li>
                    <li class="inline-flex items-center gap-2">
                        <CircleMinusIcon class="size-4 text-primary-carrot shrink-0" />
                        {{ __('user-permissions-legend-removed') }}
                    </li>
                    <li class="inline-flex items-center gap-2">
                        <RotateCcwIcon class="size-4 text-gray-dark shrink-0" />
                        {{ __('user-permissions-legend-reset') }}
                    </li>
                </ul>
            </div>

            <PanelTabs class="mt-6">
                <!-- Summary: read-only roll-up of the user's effective access,
                     highlighting where it diverges from the role defaults. -->
                <PanelTab :label="__('user-summary-tab')" default>
                    <div
                        class="mb-6 inline-flex items-center gap-2 text-body"
                        :class="hasOverride ? 'text-primary-carrot' : 'text-gray-dark'"
                    >
                        <ShieldAlertIcon v-if="hasOverride" class="size-4 shrink-0" />
                        <ShieldCheckIcon v-else class="size-4 shrink-0" />
                        {{
                            hasOverride
                                ? __('user-summary-has-overrides')
                                : __('user-summary-no-overrides')
                        }}
                    </div>

                    <PermissionsSummary
                        v-if="moduleDefinitions"
                        :permissions="moduleDefinitions"
                        :effective-permissions="userPermissions"
                        :role-permissions="compareToPermissions"
                        :groups="accessGroups"
                        :baseline-by-id="orgBaselineById"
                        :access-level="accessLevel"
                        :role-access-level="roleAccessLevel"
                        :user-name="userDisplayName"
                        :role-name="selectedRoleName"
                    />

                    <div class="mt-6 pt-6 border-t border-gray-lightest">
                        <EchoButton
                            variant="secondary"
                            :disabled="!hasOverride"
                            @click="emit('reset-overrides')"
                        >
                            {{ __('reset-user-overrides') }}
                            <template #icon-right><RotateCcwIcon /></template>
                        </EchoButton>
                    </div>
                </PanelTab>

                <!-- Data: org-tree (data access) override -->
                <PanelTab :label="__('role-tab-data')" v-if="hasRole(Role.SYSOP)">
                    <OrgDataAccessControl
                        v-model:access-level="accessLevel"
                        v-model:groups="accessGroups"
                        v-model:agent-groups="ownTeamAgg"
                        :baseline-by-id="orgBaselineById"
                        :baseline-agent-groups="roleAgentGroups"
                        :custom-baseline="roleOrgBaseline"
                        show-agent-groups
                        subject="user"
                    />
                </PanelTab>

                <!-- Functionality: module permission override -->
                <PanelTab :label="__('role-tab-functionality')">
                    <ModuleAccessControl
                        v-if="moduleDefinitions"
                        v-model:role-permissions="userPermissions"
                        :permissions="moduleDefinitions"
                        :compare-to="compareToPermissions"
                        :user-name="userDisplayName"
                        :role-name="selectedRoleName"
                        @reset-area="resetArea"
                    />
                </PanelTab>
            </PanelTabs>
        </section>
    </form>
</template>
